import React from "react";
import { Button, Stack, Typography } from "@mui/material";

import type { AssessmentDefinition } from "../types";
import { innerCard } from "../styled";

interface AssessmentBannerProps {
  definition: AssessmentDefinition;
  onViewScores: () => void;
  onAddAssessment: () => void;
  hideActions?: boolean;
  hideSecondaryAction?: boolean;
  hidePrimaryAction?: boolean;
  secondaryActionLabelOverride?: string;
  surface?: "default" | "card";
  /** Called when the secondary action should open a custom dialog (e.g., QABF) instead of a fillout URL */
  onSecondaryActionClick?: () => void;
  /** Disables the secondary action button (e.g. when a task is already pending) */
  secondaryActionDisabled?: boolean;
  /** Overrides definition.filloutUrl (e.g., to append a client guid query param) */
  filloutUrlOverride?: string;
}

export default function AssessmentBanner({
  definition,
  onViewScores,
  onAddAssessment,
  hideActions,
  hideSecondaryAction,
  hidePrimaryAction,
  secondaryActionLabelOverride,
  surface = "default",
  onSecondaryActionClick,
  secondaryActionDisabled,
  filloutUrlOverride,
}: AssessmentBannerProps) {
  const cardSurfaceStyles =
    surface === "card"
      ? {
          backgroundColor: "transparent",
          border: "none",
          boxShadow: "none",
          marginBottom: 0,
        }
      : {};

  const resolvedFilloutUrl = filloutUrlOverride ?? definition.filloutUrl;

  const secondaryActionButton =
    !hideSecondaryAction &&
    (definition.secondaryAction === "start" && resolvedFilloutUrl && !onSecondaryActionClick ? (
      <Button
        color="secondary"
        size="small"
        href={resolvedFilloutUrl}
        target="_blank"
        rel="noopener noreferrer"
        disabled={secondaryActionDisabled}
      >
        {secondaryActionLabelOverride || definition.secondaryActionLabel}
      </Button>
    ) : definition.secondaryAction === "start" && onSecondaryActionClick ? (
      <Button
        color="secondary"
        size="small"
        onClick={(event) => {
          event.currentTarget.blur();
          onSecondaryActionClick();
        }}
        disabled={secondaryActionDisabled}
      >
        {secondaryActionLabelOverride || definition.secondaryActionLabel}
      </Button>
    ) : (
      <Button
        color="secondary"
        size="small"
        onClick={(event) => {
          event.currentTarget.blur();
          onAddAssessment();
        }}
        disabled={secondaryActionDisabled}
      >
        {secondaryActionLabelOverride || definition.secondaryActionLabel}
      </Button>
    ));

  return (
    <Stack
      component="aside"
      direction={{ xs: "column", md: "row" }}
      justifyContent="space-between"
      alignItems={{ xs: "flex-start", md: "flex-start" }}
      sx={{
        ...innerCard,
        ...cardSurfaceStyles,
        p: "16px",
        rowGap: "16px",
        columnGap: "64px",
      }}
    >
      <Stack direction="column" sx={{ flex: "1 1 0", minWidth: 0 , maxWidth: { xs: "calc(100vw - 48px)", md: "none" } }}>
        <Typography variant="h3">{definition.title}</Typography>
        <Typography variant="body1" color="text.secondary">
          {definition.description}
        </Typography>
      </Stack>

      {!hideActions && (
        <Stack
          direction="row"
          sx={{
            flex: { xs: "0 0 auto", md: "0 0 auto" },
            width: { xs: "100%", md: "auto" },
            columnGap: "8px",
            whiteSpace: "nowrap",
          }}
        >
          {secondaryActionButton}

          {!hidePrimaryAction && (
            <Button color="secondary" size="small" onClick={onViewScores}>
              {definition.primaryActionLabel}
            </Button>
          )}
        </Stack>
      )}
    </Stack>
  );
}
